import type {To} from '@github-ui/react-router'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {useConfirm} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {omit} from '../../utils/omit'
import type {MemexProjectColumnId} from '../api/columns/contracts/memex-column'
import {
  CommandPaletteUI,
  KeyboardShortcut,
  type StatViewsCreateNew,
  type StatViewsDelete,
  type StatViewsDuplicate,
  type StatViewsOpen,
  type StatViewsRename,
  type StatViewsReset,
  type StatViewsSave,
  type StatViewsSaveAsNew,
  ViewsCreate,
  ViewsDelete,
  ViewsDuplicate,
  ViewsOpen,
  ViewsRename,
  ViewsReset,
  ViewsSave,
  ViewsSaveAsNew,
} from '../api/stats/contracts'
import {DefaultOmitPropertiesForView, type PageView, type ViewTypeParam} from '../api/view/contracts'
import type {ItemSpec} from '../commands/command-tree'
import {useCommands} from '../commands/hook'
import {getInitialState} from '../helpers/initial-state'
import {getViewTypeFromViewTypeParam, ViewType} from '../helpers/view-type'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {useLazyRef} from '../hooks/common/use-lazy-ref'
import {usePostStats} from '../hooks/common/use-post-stats'
import {useProjectViewRouteMatch} from '../hooks/use-project-view-route-match'
import {useCreateView, useDestroyView, useUpdateView, useUpdateViewWithoutSettingStates} from '../hooks/use-view-apis'
import {getPathDescriptor} from '../hooks/use-view-state-reducer/get-path-descriptor'
import {getViewDirtyStates} from '../hooks/use-view-state-reducer/get-view-dirty-states'
import {useViewStateReducer} from '../hooks/use-view-state-reducer/use-view-state-reducer'
import {ViewStateActionTypes} from '../hooks/use-view-state-reducer/view-state-action-types'
import {type LoadedFieldIds, ViewContext} from '../hooks/use-views'
import {useNavigate} from '../router'
import {useProjectRouteParams} from '../router/use-project-route-params'
import {PROJECT_ROUTE, PROJECT_VIEW_ROUTE} from '../routes'
import {buildInitialLoadedColumnIds} from '../state-providers/columns/columns-state-provider'
import {Resources} from '../strings'

export function ViewProvider({children}: {children: React.ReactNode}) {
  const navigate = useNavigate()

  const {
    projectLimits: {viewsLimit},
  } = getInitialState()
  const {postStats} = usePostStats()
  const {currentView, views, viewsMap, viewStateDispatch} = useViewStateReducer()
  const {hasWritePermissions} = ViewerPrivileges()

  const reorderView = useCallback(
    (viewNumber: number, prevViewNumber?: number) => {
      viewStateDispatch({
        type: ViewStateActionTypes.ReorderView,
        viewNumber,
        prevViewNumber,
      })
    },
    [viewStateDispatch],
  )

  const setCurrentViewNumber = useCallback(
    (viewNumber: number, stats?: Omit<StatViewsOpen, 'name'>) => {
      viewStateDispatch({
        type: ViewStateActionTypes.GoToViewNumber,
        viewNumber,
      })
      setTimeout(() => {
        if (stats) {
          postStats({name: ViewsOpen, memexProjectViewNumber: viewNumber, ...stats})
        }
      }, 0)
    },
    [postStats, viewStateDispatch],
  )

  const addViewToState = useCallback(
    (view: PageView) => {
      viewStateDispatch({
        type: ViewStateActionTypes.AddView,
        view,
      })
    },
    [viewStateDispatch],
  )

  const updateViewInState = useCallback(
    (view: PageView) => {
      viewStateDispatch({
        type: ViewStateActionTypes.UpdateView,
        view,
      })
    },
    [viewStateDispatch],
  )

  const deleteViewFromState = useCallback(
    (viewNumber: number) => {
      viewStateDispatch({
        type: ViewStateActionTypes.DeleteView,
        viewNumber,
      })
    },
    [viewStateDispatch],
  )

  const updateViewServerStates = useCallback(
    (viewStates: Array<PageView>) => {
      viewStateDispatch({
        type: ViewStateActionTypes.SetViewServerStates,
        viewStates,
      })
    },
    [viewStateDispatch],
  )

  const createView = useCreateView({
    setCurrentViewNumber,
    addView: addViewToState,
    updateView: updateViewInState,
    deleteView: deleteViewFromState,
  })

  const createNewDefaultView = useCallback(
    (overrides: Partial<{layout: ViewTypeParam}>, stats: Omit<StatViewsCreateNew, 'name' | 'context'>) => {
      // If we already have an in-progress request, don't create a new view
      if (createView.status.current.status === 'loading') {
        return
      }
      const creator = createView.perform({
        view: overrides,
      })
      postStats({
        name: ViewsCreate,
        memexProjectViewNumber: currentView?.number,
        context: overrides.layout ? getViewTypeFromViewTypeParam(overrides.layout) : ViewType.Table,
        ...stats,
      })
      return creator
    },
    [createView, currentView?.number, postStats],
  )

  const updateView = useUpdateView({
    setCurrentViewNumber,
    addView: addViewToState,
    updateView: updateViewInState,
    deleteView: deleteViewFromState,
  })

  const destroyView = useDestroyView({
    setCurrentViewNumber,
    addView: addViewToState,
    updateView: updateViewInState,
    deleteView: deleteViewFromState,
  })

  const updateViewWithoutSettingStates = useUpdateViewWithoutSettingStates()

  const resetViewState = useCallback(
    (viewNumber: number, stats?: Omit<StatViewsReset, 'name'>, resetScope?: 'all' | 'filter' | 'config') => {
      viewStateDispatch({type: ViewStateActionTypes.ResetViewState, viewNumber, resetScope})
      if (stats) {
        postStats({
          name: ViewsReset,
          memexProjectViewNumber: viewNumber,
          ...stats,
        })
      }
    },
    [postStats, viewStateDispatch],
  )

  const setViewNumberWithReset = useCallback(
    (viewNumber: number) => {
      if (currentView && currentView.isDeleted) {
        deleteViewFromState(currentView.number)
      } else if (currentView) {
        resetViewState(currentView.number)
      }

      setCurrentViewNumber(viewNumber)
    },
    [deleteViewFromState, resetViewState, setCurrentViewNumber, currentView],
  )

  const createViewWithReset = useCreateView({
    setCurrentViewNumber: setViewNumberWithReset,
    addView: addViewToState,
    updateView: updateViewInState,
    deleteView: deleteViewFromState,
  })

  //#region View name
  const renameView = useCallback(
    async (viewNumber: number, newName: string, stats: Omit<StatViewsRename, 'name'>) => {
      const view = viewsMap[viewNumber]
      if (!view || view.isDeleted) return

      if (newName !== view.name) {
        const params = omit(view.serverViewState, DefaultOmitPropertiesForView)
        await updateViewWithoutSettingStates.perform({
          viewNumber: view.number,
          view: {
            ...params,
            name: newName,
            filter: view.serverViewState.filter ?? '',
          },
        })
        postStats({
          name: ViewsRename,
          memexProjectViewNumber: viewNumber,
          ...stats,
        })

        if (updateViewWithoutSettingStates.status.current.status === 'succeeded') {
          viewStateDispatch({type: ViewStateActionTypes.SaveViewName, viewNumber, name: newName})
        }
      }
    },
    [viewsMap, updateViewWithoutSettingStates, viewStateDispatch, postStats],
  )
  //#endregion

  const saveCurrentViewState = useCallback(
    async (viewNumber: number, stats: Omit<StatViewsSave, 'name'>, saveScope?: 'all' | 'filter' | 'config') => {
      const view = viewsMap[viewNumber]

      if (!view || view.isDeleted) return

      let updateViewReq

      if (!saveScope || saveScope === 'all') {
        // Save everything (original behavior)
        updateViewReq = omit(view.localViewState, DefaultOmitPropertiesForView)
      } else if (saveScope === 'filter') {
        // Save only the filter preserve all pending config changes
        updateViewReq = omit(
          {
            ...view.serverViewState,
            filter: view.localViewState.filter,
          },
          DefaultOmitPropertiesForView,
        )
      } else {
        // saveScope === 'config'
        // Save only the display config preserve all pending filter changes
        updateViewReq = omit(
          {
            ...view.localViewState,
            filter: view.serverViewState.filter,
          },
          DefaultOmitPropertiesForView,
        )
      }

      // For selective saves, use SaveViewSelective action to preserve unsaved changes
      if (saveScope && saveScope !== 'all') {
        await updateViewWithoutSettingStates.perform({viewNumber, view: updateViewReq})

        if (updateViewWithoutSettingStates.status.current.status === 'succeeded') {
          const updatedView = updateViewWithoutSettingStates.status.current.data
          viewStateDispatch({
            type: ViewStateActionTypes.SaveViewSelective,
            view: updatedView,
            saveScope,
          })
        }
      } else {
        // For full saves or when saveScope is undefined, use the original behavior
        await updateView.perform({viewNumber, view: updateViewReq})
      }
      postStats({name: ViewsSave, memexProjectViewNumber: viewNumber, ...stats})
    },
    [viewsMap, updateView, updateViewWithoutSettingStates, viewStateDispatch, postStats],
  )

  const duplicateCurrentViewState = useCallback(
    async (
      viewNumber: number,
      name: string | undefined,
      stats: Omit<StatViewsSaveAsNew, 'name'> | Omit<StatViewsDuplicate, 'name'>,
    ) => {
      const view = viewsMap[viewNumber]
      if (!view) throw new Error('No view found')
      if (Object.keys(viewsMap).length > viewsLimit) return
      const dirty = getViewDirtyStates(view)
      const duplicateViewReqBody = omit(view.localViewState, [...DefaultOmitPropertiesForView, 'name'])

      postStats(
        dirty.isViewStateDirty
          ? {name: ViewsSaveAsNew, memexProjectViewNumber: viewNumber, ...stats}
          : {name: ViewsDuplicate, memexProjectViewNumber: viewNumber, ...stats},
      )

      return createViewWithReset.perform({view: {...duplicateViewReqBody, name}})
    },
    [viewsMap, createViewWithReset, postStats, viewsLimit],
  )

  const confirm = useConfirm()

  const destroyCurrentView = useCallback(
    async (viewNumber: number, stats: Omit<StatViewsDelete, 'name'>, cb?: () => void) => {
      const shouldDestroy = await confirm({
        title: 'Delete view?',
        content: 'Are you sure you want to delete this view?',
        confirmButtonContent: 'Delete',
        confirmButtonType: 'danger',
      })
      const view = viewsMap[viewNumber]
      if (shouldDestroy) {
        if (view?.isDeleted) {
          deleteViewFromState(viewNumber)
          cb?.()
          return
        }
        await destroyView.perform({viewNumber})
        postStats({name: ViewsDelete, memexProjectViewNumber: viewNumber, ...stats})
      }
      cb?.()
    },
    [confirm, viewsMap, destroyView, postStats, deleteViewFromState],
  )

  const {isProjectViewRoute} = useProjectViewRouteMatch()

  const updateViewPriority = useCallback(
    async (
      viewNumber: number,
      previousViewId: number | string,
      previousViewNumber?: number,
      oldPreviousViewNumber?: number,
    ) => {
      const view = viewsMap[viewNumber]
      if (!view || view.isDeleted) return
      const params = omit(view.serverViewState, DefaultOmitPropertiesForView)

      // Optimistically update the view order in the local state
      reorderView(viewNumber, previousViewNumber)

      try {
        // The local state update we want to make has already been performed, so replacing
        // the local state with the server response is unnecessary and could cause any
        // unsaved changes to be lost (ref github/memex#6554).
        await updateViewWithoutSettingStates.perform({
          viewNumber,
          view: {
            ...params,
            previousMemexProjectViewId: previousViewId,
          },
        })
      } catch {
        // if it fails, reorder again to previous state
        reorderView(viewNumber, oldPreviousViewNumber)
      }
    },
    [viewsMap, updateViewWithoutSettingStates, reorderView],
  )

  const trackingCurrentView = useTrackingRef(currentView)
  const saveCurrentView = useCallback(() => {
    const view = trackingCurrentView.current
    if (!view || !view.isViewStateDirty || view.isDeleted) return
    saveCurrentViewState(view.number, {ui: KeyboardShortcut})
  }, [saveCurrentViewState, trackingCurrentView])

  const projectRouteParams = useProjectRouteParams()

  useCommands(() => {
    if (!currentView) return null

    const commands: Array<ItemSpec> = []

    if (isProjectViewRoute && currentView.isViewStateDirty && !currentView.isDeleted) {
      if (hasWritePermissions) {
        commands.push([
          's',
          'Save view',
          'views_save',
          () => saveCurrentViewState(currentView.number, {ui: CommandPaletteUI}),
        ])
      }

      commands.push([
        'r',
        'Reset view',
        'views_reset',
        () => resetViewState(currentView.number, {ui: CommandPaletteUI}),
      ])
    }

    if (views.length > 1) {
      if (isProjectViewRoute && hasWritePermissions) {
        commands.push([
          'd',
          'Delete view',
          'views_delete',
          () => destroyCurrentView(currentView.number, {ui: CommandPaletteUI}),
        ])
      }

      commands.push([
        'g',
        'Go to view...',
        views.reduce<Array<ItemSpec>>((goToCommands, view, index) => {
          /**
           * If we're looking at a project view, don't add a command to
           * navigate to the current view
           */
          if (isProjectViewRoute && view.number === currentView.number) {
            return goToCommands
          }

          goToCommands.push([
            `${index}`,
            `Go to ${view.name}`,
            'views_change',
            () => {
              setCurrentViewNumber(view.number, {ui: CommandPaletteUI})
              /**
               * If we're not looking at a project view, don't add a command to
               * navigate to the project view, instead of just changing it
               */
              if (!isProjectViewRoute) {
                navigate(
                  PROJECT_VIEW_ROUTE.generatePath({
                    ...projectRouteParams,
                    viewNumber: view.number,
                  }),
                )
              }
            },
          ])
          return goToCommands
        }, []),
      ])
    }

    if (isProjectViewRoute && views.length < viewsLimit && hasWritePermissions) {
      commands.push(['n', 'New view', 'views_create', () => createNewDefaultView({}, {ui: CommandPaletteUI})])
      commands.push([
        'c',
        Resources.duplicateView({isDirty: currentView.isViewStateDirty}),
        'views_duplicate',
        () => duplicateCurrentViewState(currentView.number, undefined, {ui: CommandPaletteUI}),
      ])
    }

    if (commands.length === 0) return null

    return ['v', 'View options...', commands]
  }, [
    createNewDefaultView,
    currentView,
    destroyCurrentView,
    duplicateCurrentViewState,
    hasWritePermissions,
    isProjectViewRoute,
    navigate,
    resetViewState,
    saveCurrentViewState,
    setCurrentViewNumber,
    views,
    viewsLimit,
    projectRouteParams,
  ])

  const returnToViewLinkTo: To = useMemo(() => {
    if (currentView) {
      return getPathDescriptor(currentView, projectRouteParams)
    }
    return PROJECT_ROUTE.generatePath(projectRouteParams)
  }, [currentView, projectRouteParams])

  const loadedFieldIdsByViewRef = useLazyRef<Record<number, LoadedFieldIds>>(() => {
    if (!currentView) return {}
    const fieldIds = buildInitialLoadedColumnIds()
    return {[currentView.id]: fieldIds}
  })

  const addLoadedFieldIdForCurrentView = useCallback(
    (newColumnId: MemexProjectColumnId) => {
      const viewId = currentView?.id
      if (!viewId) return
      if (loadedFieldIdsByViewRef.current[viewId]) {
        loadedFieldIdsByViewRef.current[viewId].add(newColumnId)
      } else {
        // This lint warning is a false positive and is fixed in React v19
        // https://github.com/facebook/react/issues/29196

        loadedFieldIdsByViewRef.current[viewId] = new Set([newColumnId])
      }
    },
    [currentView?.id, loadedFieldIdsByViewRef],
  )

  return (
    <ViewContext.Provider
      value={useMemo(() => {
        return {
          views,
          viewsMap,
          viewStateDispatch,
          createView,
          currentView,
          duplicateCurrentViewState,
          setCurrentViewNumber,
          updateView,
          updateViewPriority,
          saveCurrentViewState,
          destroyView,
          isViewStateDirty: currentView?.isViewStateDirty ?? false,
          resetViewState,
          createNewDefaultView,
          destroyCurrentView,
          renameView,
          returnToViewLinkTo,
          updateViewServerStates,
          loadedFieldIdsByViewRef,
          addLoadedFieldIdForCurrentView,
        }
      }, [
        views,
        viewsMap,
        viewStateDispatch,
        createView,
        currentView,
        duplicateCurrentViewState,
        setCurrentViewNumber,
        updateView,
        updateViewPriority,
        saveCurrentViewState,
        destroyView,
        resetViewState,
        createNewDefaultView,
        destroyCurrentView,
        renameView,
        returnToViewLinkTo,
        updateViewServerStates,
        loadedFieldIdsByViewRef,
        addLoadedFieldIdForCurrentView,
      ])}
    >
      <GlobalCommands
        commands={{
          'projects:save-view':
            isProjectViewRoute && hasWritePermissions && !currentView?.isDeleted && currentView?.isViewStateDirty
              ? saveCurrentView
              : undefined,
        }}
      />
      {children}
    </ViewContext.Provider>
  )
}

try{ ViewProvider.displayName ||= 'ViewProvider' } catch {}